(function attachEaGroupModel(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.EaGroupModel = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createEaGroupModel() {
  const COLORS = [
    '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
    '#f97316', '#14b8a6', '#e879f9', '#84cc16', '#6366f1', '#ec4899'
  ];

  function isFalse(value) {
    return value === false || value === 0 || String(value).toLowerCase() === 'false';
  }

  function isTrue(value) {
    return value === true || value === 1 || String(value).toLowerCase() === 'true';
  }

  function normalizeTitle(value) {
    return String(value || '').trim().toLocaleUpperCase('tr-TR');
  }

  function uniqueStrings(values) {
    return [...new Set((values || []).map(value => String(value)).filter(Boolean))];
  }

  function ownDeviceIds(node) {
    const devices = node && node.data && Array.isArray(node.data.ea_devices)
      ? node.data.ea_devices
      : [];
    return uniqueStrings(devices);
  }

  function collectDeviceIds(node, target = [], seen = new Set()) {
    if (!node) return target;
    ownDeviceIds(node).forEach(deviceId => {
      if (!seen.has(deviceId)) {
        seen.add(deviceId);
        target.push(deviceId);
      }
    });
    (Array.isArray(node.children) ? node.children : []).forEach(child => {
      collectDeviceIds(child, target, seen);
    });
    return target;
  }

  function collectIncludedDeviceIds(node, target = [], seen = new Set()) {
    if (!node) return target;
    ownDeviceIds(node).forEach(deviceId => {
      if (!seen.has(deviceId)) {
        seen.add(deviceId);
        target.push(deviceId);
      }
    });
    (Array.isArray(node.children) ? node.children : [])
      .filter(includeChildInParent)
      .forEach(child => collectIncludedDeviceIds(child, target, seen));
    return target;
  }

  function isExplicitEaPage(node) {
    return !!(node && node.data && isTrue(node.data.ea_page));
  }

  function isAutomaticGroupRoot(node) {
    if (!node) return false;
    const data = node.data || {};
    if (data.ea_group_page !== undefined) return isTrue(data.ea_group_page);
    const title = normalizeTitle(data.title || node.text);
    return title === 'GRUP BAZLI' && collectDeviceIds(node).length > 0;
  }

  function isEaHierarchyNode(node) {
    return isExplicitEaPage(node) || isAutomaticGroupRoot(node);
  }

  function normalizeTagVisibilitySettings(settings) {
    const normalized = {};
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return normalized;

    Object.entries(settings).forEach(([tagName, visibility]) => {
      const key = String(tagName || '').trim();
      if (!key || key.length > 512 || !visibility || typeof visibility !== 'object' || Array.isArray(visibility)) return;
      if (typeof visibility.useChartTag !== 'boolean' || typeof visibility.useEndexTag !== 'boolean') return;
      Object.defineProperty(normalized, key, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: {
          useChartTag: visibility.useChartTag,
          useEndexTag: visibility.useEndexTag
        }
      });
    });

    return normalized;
  }

  function applyTagVisibilitySettings(items, settings) {
    const normalized = normalizeTagVisibilitySettings(settings);
    return (Array.isArray(items) ? items : []).map(item => {
      if (!item || typeof item !== 'object') return item;
      const tagName = String(item.TagName || item.TagId || '').trim();
      const visibility = Object.prototype.hasOwnProperty.call(normalized, tagName) ? normalized[tagName] : null;
      if (!visibility) return item;
      return {
        ...item,
        useChartTag: visibility.useChartTag,
        useEndexTag: visibility.useEndexTag
      };
    });
  }

  function includeChildInParent(child) {
    const data = child && child.data ? child.data : {};
    if (data.ea_include_in_parent !== undefined) return !isFalse(data.ea_include_in_parent);
    return true;
  }

  function asDeviceMap(deviceGroups) {
    if (deviceGroups instanceof Map) return deviceGroups;
    return new Map(Object.entries(deviceGroups || {}).map(([id, group]) => [String(id), group]));
  }

  function buildDrilldownEntries(node, deviceGroups) {
    const data = node && node.data ? node.data : {};
    const drilldownEnabled = !isFalse(data.ea_drilldown);
    const children = drilldownEnabled && Array.isArray(node && node.children)
      ? node.children.filter(includeChildInParent)
      : [];

    return children.map(child => ({
        key: `node:${String(child.id || child.data && child.data.parentId || child.text)}`,
        label: String(child.data && child.data.title || child.text || 'EA Grubu'),
        node: child,
        deviceIds: collectIncludedDeviceIds(child)
      }));
  }

  function hasPowerDistribution(node) {
    return buildDrilldownEntries(node).length > 0;
  }

  function findTagBySuffix(tags, suffixes) {
    const list = Array.isArray(suffixes) ? suffixes : [suffixes];
    for (const suffix of list) {
      const normalized = String(suffix || '').toUpperCase();
      const found = (tags || []).find(tag => String(tag.TagName || '').toUpperCase().endsWith(`_${normalized}`));
      if (found) return found.TagName;
    }
    return null;
  }

  function resolvePowerSource(deviceGroup) {
    const tags = deviceGroup && Array.isArray(deviceGroup.tags) ? deviceGroup.tags : [];
    const totalTag = findTagBySuffix(tags, 'TIAP');
    if (totalTag) return { mode: 'total', tags: [totalTag] };
    const phaseTags = ['P1', 'P2', 'P3'].map(suffix => findTagBySuffix(tags, suffix)).filter(Boolean);
    return { mode: 'phases', tags: phaseTags };
  }

  function resolveEnergySource(deviceGroup) {
    const tags = deviceGroup && Array.isArray(deviceGroup.tags) ? deviceGroup.tags : [];
    // Legacy PLC exports keep the usable counter in IAE1b while IAE1a can
    // remain as an unpolled DataMap placeholder for the old high-order word.
    const suffixTag = findTagBySuffix(tags, ['ENERGY', 'IAE', 'IAE1B', 'IAE1A', 'IAE1C']);
    if (suffixTag) return suffixTag;
    const unitMatch = tags.find(tag => String(tag.Birim || tag.Unit || '').toUpperCase().replace(/[^A-Z]/g, '') === 'KWH');
    return unitMatch ? unitMatch.TagName : null;
  }

  function toNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (value === null || value === undefined || value === '') return null;
    let normalized = String(value).trim().replace(/\s/g, '');
    if (normalized.includes(',') && normalized.includes('.')) normalized = normalized.replace(/\./g, '').replace(',', '.');
    else normalized = normalized.replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function readValue(valueMap, tagName) {
    if (!tagName) return null;
    const raw = valueMap instanceof Map ? valueMap.get(tagName) : valueMap && valueMap[tagName];
    return toNumber(raw && typeof raw === 'object' && 'Value' in raw ? raw.Value : raw);
  }

  function calculateDevicePower(deviceGroup, valueMap) {
    const source = resolvePowerSource(deviceGroup);
    const values = source.tags.map(tagName => readValue(valueMap, tagName)).filter(value => value !== null);
    if (values.length === 0) return { available: false, value: 0, source };
    return { available: true, value: values.reduce((sum, value) => sum + value, 0), source };
  }

  function calculateDistribution(entries, deviceGroups, valueMap) {
    const groups = asDeviceMap(deviceGroups);
    const rows = (entries || []).map((entry, index) => {
      let signedValue = 0;
      let availableDevices = 0;
      entry.deviceIds.forEach(deviceId => {
        const result = calculateDevicePower(groups.get(String(deviceId)), valueMap);
        if (result.available) {
          availableDevices += 1;
          signedValue += result.value;
        }
      });
      return {
        ...entry,
        signedValue,
        chartValue: Math.abs(signedValue),
        availableDevices,
        missingDevices: Math.max(0, entry.deviceIds.length - availableDevices),
        direction: signedValue < 0 ? 'generation' : 'consumption',
        color: COLORS[index % COLORS.length]
      };
    });
    const chartTotal = rows.reduce((sum, row) => sum + row.chartValue, 0);
    rows.forEach(row => { row.percent = chartTotal > 0 ? row.chartValue / chartTotal * 100 : 0; });
    return {
      rows,
      chartTotal,
      netTotal: rows.reduce((sum, row) => sum + row.signedValue, 0),
      consumptionTotal: rows.reduce((sum, row) => sum + Math.max(0, row.signedValue), 0),
      generationTotal: rows.reduce((sum, row) => sum + Math.abs(Math.min(0, row.signedValue)), 0)
    };
  }

  function calculateNodeTotals(node, deviceGroups, valueMap) {
    const deviceIds = collectDeviceIds(node);
    const entries = deviceIds.map(deviceId => ({ key: `node-device:${deviceId}`, deviceIds: [deviceId] }));
    const distribution = calculateDistribution(entries, deviceGroups, valueMap);
    return {
      deviceCount: deviceIds.length,
      availableDevices: distribution.rows.reduce((sum, row) => sum + row.availableDevices, 0),
      missingDevices: distribution.rows.reduce((sum, row) => sum + row.missingDevices, 0),
      consumptionTotal: distribution.consumptionTotal,
      generationTotal: distribution.generationTotal,
      netTotal: distribution.netTotal
    };
  }

  return {
    applyTagVisibilitySettings,
    buildDrilldownEntries,
    calculateDevicePower,
    calculateDistribution,
    calculateNodeTotals,
    collectDeviceIds,
    collectIncludedDeviceIds,
    findTagBySuffix,
    hasPowerDistribution,
    includeChildInParent,
    isAutomaticGroupRoot,
    isEaHierarchyNode,
    isExplicitEaPage,
    normalizeTagVisibilitySettings,
    ownDeviceIds,
    resolvePowerSource,
    resolveEnergySource,
    toNumber
  };
});
